import React, { ReactNode, FC, CSSProperties, memo, useMemo, HTMLAttributes } from "react";
import { Button, Modal, ModalDialogProps, ModalProps, Spinner } from "react-bootstrap";
import IconButton from "../IconButton/IconButton";
import styles from "./CustomModal.module.scss";
import { useAnimatedHeight } from "./useAnimatedHeight";
import { useIntl } from "react-intl";

export interface CustomModalProps extends ModalProps {
  title?: string;
  titleStyle?: CSSProperties;
  titleClass?: string;
  open: boolean;
  openModal?: () => void;
  headerClassName?: string;
  footerClassName?: string;
  setOpen?: (value: boolean) => void;
  closeModal?: () => void;
  children: ReactNode;
  handleOnClose?: (node: HTMLElement) => any;
  cancelButtonText?: string;
  submitButtonText?: string;
  renderFooterButtons?: ReactNode;
  renderHeader?: ReactNode;
  noHeader?: boolean;
  noFooter?: boolean;
  onSubmit?: (data?: any) => void;
  disableSubmit?: boolean;
  onCancel?: () => void;
  noCloseIcon?: boolean;
  fullscreen?: string | true;
  isLoading?: boolean;
  loadingText?: string;
  width?: string;
  bodyStyle?: CSSProperties;
  bodyClassName?: string;
  dialogClassName?: string;
  noCancelButton?: boolean;
  dialogProps?: ModalDialogProps;
  modalPosition?: "top" | "bottom" | "right" | "left";
  bodyProps?: HTMLAttributes<HTMLDivElement>;
  subTitle?: string;
  subTitleStyle?: CSSProperties;
  subTitleClass?: string;
  /**
   * Caps the dialog at 90vh (min 500px) and scrolls the body instead of the page, so the
   * header and footer stay visible on a form too tall for the viewport. Off by default -
   * a short modal should still size to its content.
   */
  scrollable?: boolean;
  /**
   * Grows and shrinks the dialog smoothly when its content changes height - a form swapping one
   * set of fields for another - instead of jumping (see `useAnimatedHeight`). On by default; pass
   * `false` for a dialog whose content resizes too often or too wildly to follow, such as one that
   * holds a grid sizing itself to its rows. Never applies to a full-screen dialog, whose height is
   * the window's rather than its content's.
   */
  animateHeight?: boolean;
  // Add other props if necessary
}

const modalPositionStyles = {};

const renderModalDialog =
  ({
    modalPosition,
    width,
    fullscreen,
    dialogProps,
    scrollable,
  }: {
    modalPosition?: "top" | "bottom" | "right" | "left";
    width?: string;
    fullscreen?: string | true | undefined;
    dialogProps?: ModalDialogProps;
    scrollable?: boolean;
  }) =>
  (dProps?: ModalDialogProps) => {
    // Merge the dialogProps with dProps, with dProps taking precedence
    const mergedProps = {
      ...dialogProps,
      ...dProps,
      className: `${dProps?.className || ""} ${dialogProps?.className || ""}`.trim(),
    };

    return (
      <div
        {...mergedProps}
        className={`modal-dialog modal-dialog-centered ${
          fullscreen === true
            ? `w-lg-100 modal-fullscreen ${styles.customModalDialogFullscreen}`
            : `${width === "700px" ? styles.customModalDialog : `min-w-lg-${width}`}`
        } ${modalPosition ? styles?.[`modalDialog-${modalPosition}`] : ""} ${
          scrollable ? styles.scrollableDialog : ""
        } ${mergedProps.className || ""}`}
      />
    );
  };

const CustomModal: FC<CustomModalProps> = ({
  title,
  titleStyle = {},
  titleClass = "",
  open,
  openModal,
  setOpen,
  closeModal,
  children,
  handleOnClose,
  cancelButtonText,
  submitButtonText,
  renderFooterButtons,
  noFooter = false,
  noHeader = false,
  renderHeader,
  onSubmit,
  disableSubmit = false,
  onCancel,
  noCloseIcon = false,
  fullscreen,
  isLoading = false,
  width = "700px",
  loadingText = "Saving...</div>",
  bodyClassName = "",
  noCancelButton = false,
  dialogClassName = "",
  bodyStyle = {},
  modalPosition,
  footerClassName,
  headerClassName,
  dialogProps,
  centered,
  contentClassName,
  bodyProps,
  subTitle,
  subTitleStyle = {},
  subTitleClass = "",
  scrollable = false,
  animateHeight = true,
  ...props
}: CustomModalProps) => {
  const { $t } = useIntl();
  const contentRef = useAnimatedHeight(animateHeight && fullscreen !== true, styles.animatingHeight);

  const handleSubmit = () => {
    if (onSubmit) onSubmit();
  };

  // Memoize the dialog component to prevent unnecessary re-renders
  const _renderDialog = useMemo(
    () =>
      renderModalDialog({
        fullscreen: fullscreen || "sm-down",
        modalPosition,
        width,
        dialogProps,
        scrollable,
      }),
    [dialogProps, fullscreen, modalPosition, width, scrollable]
  );

  return (
    <Modal
      show={open}
      onExit={handleOnClose}
      animation={true}
      onHide={closeModal}
      backdropClassName={styles.backdrop}
      dialogAs={_renderDialog}
      centered={centered}
      // Flatpickr appends its calendar to document.body, outside this dialog. With
      // Bootstrap's focus trap on, clicking the year/hour field pulls focus straight
      // back to the dialog, which both blocks typing and makes flatpickr treat it as
      // a click-away and close. Pass `enforceFocus` explicitly to opt back in.
      enforceFocus={false}
      {...props}
    >
      <div ref={contentRef} className={`modal-content ${fullscreen ? "w-lg-100" : ""} ${contentClassName || ""}`}>
        {isLoading && (
          <div className={styles.loader}>
            <Spinner animation="border" />
            <span>{loadingText}</span>
          </div>
        )}
        {!noHeader && (
          <Modal.Header className={`justify-content-between ${headerClassName}` || "justify-content-between"}>
            {renderHeader ? (
              renderHeader
            ) : (
              <>
                <div className="d-flex flex-column align-items-start">
                  {title && (
                    <h3 className={`fw-bolder ${titleClass} m-0`} style={titleStyle}>
                      {title}
                    </h3>
                  )}
                  {subTitle && (
                    <h6 className={` ${subTitleClass} m-0`} style={subTitleStyle}>
                      {subTitle}
                    </h6>
                  )}
                </div>
                {!noCloseIcon && (
                  <IconButton icon="cross" className="bg-primary btn-sm rounded-circle" iconClassName="text-white" iconType="outline" onClick={closeModal} />
                )}
              </>
            )}
          </Modal.Header>
        )}
        <Modal.Body {...bodyProps} style={{ ...bodyStyle }} className={bodyClassName || ""}>
          {children}
        </Modal.Body>
        {!noFooter &&
          (renderFooterButtons ? (
            <Modal.Footer className={footerClassName}>{renderFooterButtons}</Modal.Footer>
          ) : (
            <Modal.Footer>
              {!noCancelButton && (
                <Button
                  variant="flat"
                  className="text-primary btn-active-light-primary me-3"
                  onClick={closeModal}
                >
                  {cancelButtonText || $t({ id: "BUTTON.CANCEL" })}
                </Button>
              )}
              <Button variant="primary" onClick={handleSubmit} disabled={disableSubmit}>
                {submitButtonText || $t({ id: "BUTTON.SUBMIT" })}
              </Button>
            </Modal.Footer>
          ))}
      </div>
    </Modal>
  );
};

export default memo(CustomModal);
